import { toKebabCase, toPascalCase } from './utils.js';
class BlockRegistryTarget extends EventTarget {
    propagateNewBlock(blockName, blockLoader) {
        this.dispatchEvent(new CustomEvent("registration", { detail: {
                name: blockName,
                loader: blockLoader
            } }));
    }
    loadedNewBlock(block, options) {
        this.dispatchEvent(new CustomEvent("loaded", { detail: {
                block,
                options
            } }));
    }
}
const blockRegistryEventTarget = new BlockRegistryTarget();
export const addBlockRegistryListener = blockRegistryEventTarget.addEventListener.bind(blockRegistryEventTarget);
export const removeBlockRegistryListener = blockRegistryEventTarget.removeEventListener.bind(blockRegistryEventTarget);
export const blockRegistry = new Proxy({}, {
    get(target, p) {
        return target[p];
    },
    set(target, p, value) {
        if (typeof p === "symbol") {
            return false;
        }
        target[p] = (options) => new Promise(async (resolve, reject) => {
            try {
                const block = await (value(options));
                blockRegistryEventTarget.loadedNewBlock(block, options);
                resolve(block);
            }
            catch (e) {
                reject(e);
            }
        });
        target[toPascalCase(p)] = target[p];
        blockRegistryEventTarget.propagateNewBlock(p, target[p]);
        return true;
    }
});
export async function getBlock(name, options = { debug: false }) {
    let blockLoader = blockRegistry[name] ?? blockRegistry[toPascalCase(name)];
    if (blockLoader === undefined) {
        throw new Error(`Block ${name} does not exists`);
    }
    return await blockLoader(options);
}
/**
 * Register a Block type as a Gutenberg component.
 * This force registration of any block as a Smartfire block available in the page.
 * This function is handled by the compilation magic, but you can use yourself to directly register blocks.
 *
 * @param name the component identifier
 * @param component
 * @param vendor
 */
export default function registerBlock(name, component, vendor = 'smartfire') {
    // noinspection SuspiciousTypeOfGuard because of JS, we must ensure that
    if (typeof name !== 'string') {
        throw new Error('First parameter MUST be the component name');
    }
    blockRegistry[name] = async (options) => {
        try {
            const blockModule = await component();
            let block;
            if (blockModule && blockModule.hasOwnProperty('default')) {
                block = blockModule.default;
            }
            else {
                block = blockModule;
            }
            Object.defineProperty(block, "name", { value: toPascalCase(name) });
            if (block._smartfire === undefined) {
                console.warn(`Your block ${name} does not seems to be a Smartfire block, are you sure to have used the \`defineBlock\` wrapper?\nWe have skipped Gutenberg registration.`);
            }
            else {
                if (!(block._smartfire.setup?.block?.hideInEditor ?? false)) {
                    try {
                        const blockApi = await import('@wordpress/blocks');
                        const componentName = vendor + '/' + toKebabCase(name);
                        if (typeof blockApi.registerBlockType === 'function' &&
                            blockApi.getBlockTypes().filter(t => t.name === componentName).length <=
                                0) {
                            if (block._smartfire) {
                                const setup = await block._smartfire.configureGutenberg(name, vendor);
                                if (options?.debug) {
                                    console.debug(`Registering on Gutenberg component ${componentName}`, block, setup);
                                }
                                blockApi.registerBlockType(componentName, setup);
                            }
                        }
                    }
                    catch (e) {
                        if (options?.debug) {
                            console.debug("Skip gutenberg registration because of error", e);
                        }
                    }
                }
            }
            return block;
        }
        catch (e) {
            console.error("Failed to load the block due to a fatal error for block " + name);
            console.error(e);
            throw e;
        }
    };
    return blockRegistry[name];
}

//# sourceMappingURL=registerBlock.js.map
